import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  addActiveExperiments,
  removeActiveExperiments,
  selectExperimentUseNew,
  selectIsUserExperimentLoaded,
} from '../../store/userExperiment'
import { AllState } from '../../store'

/** 実験ID一覧 */
export const EXPERIMENT_IDS = ['wbs-status-column-layout'] as const

/** 実験ID */
export type ExperimentId = (typeof EXPERIMENT_IDS)[number]

type UseExperimentResult = {
  /** 新しいUIを使用するか */
  useNew: boolean
  /** 設定がロードされているか */
  isLoaded: boolean
}

/**
 * 実験を使用する画面用のフック
 * - useNew（新しいUIを使用するか）を取得
 * - バナー表示対象として自動登録（マウント時に登録、アンマウント時に解除）
 *
 * @param experimentId 実験ID
 * @param enabled 実験が有効かどうか（feature flag の結果を渡す）
 * false の場合は useNew: false を返し、バナー登録もスキップする
 */
export const useExperiment = (
  experimentId: ExperimentId,
  enabled: boolean = true
): UseExperimentResult => {
  const useNew = useSelector((state: AllState) =>
    selectExperimentUseNew(state, experimentId)
  )
  const isLoaded = useSelector((state: AllState) =>
    selectIsUserExperimentLoaded(state)
  )

  // バナー表示対象として登録（enabled の場合のみ）
  useRegisterExperiments(enabled ? [experimentId] : [])

  // enabled が false なら従来のUI
  if (!enabled) {
    return { useNew: false, isLoaded: true }
  }

  return { useNew, isLoaded }
}

/**
 * 現在の画面で対象となる実験IDを登録するフック
 * マウント時に登録、アンマウント時に解除される
 * @internal useExperiment から内部的に呼ばれる
 */
const useRegisterExperiments = (experimentIds: ExperimentId[]): void => {
  const dispatch = useDispatch()
  const experimentIdsRef = useRef(experimentIds)
  experimentIdsRef.current = experimentIds

  useEffect(() => {
    const ids = experimentIdsRef.current
    if (ids.length === 0) return

    dispatch(addActiveExperiments(ids))
    return () => {
      dispatch(removeActiveExperiments(ids))
    }
  }, [dispatch, experimentIds.length])
}
